import { ResourceRef, computed, linkedSignal, untracked } from '@angular/core'
import { rxResource, toSignal } from '@angular/core/rxjs-interop'

import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals'

import { EMPTY, Observable, distinctUntilChanged, lastValueFrom, of, switchMap } from 'rxjs'
import { catchError, finalize, map } from 'rxjs/operators'

import {
  GroupSolutionAccessAsset,
  GroupSolutionAccessAssetCandidate,
} from '@flowx/designer/modules/workspaces/shared/commons.types'
import { groupMapToAssetSummary } from '@flowx/designer/modules/workspaces/shared/transform-utils'
import {
  AccessManagementAssetSummary,
  AccessManagementGroup,
  AccessManagementGroupPayload,
  AccessManagementUser,
  AccessManagementUserSummary,
} from '@flowx/designer/modules/workspaces/shared/workspace.utils'
import { selectAllRouteParams } from '@flowx/designer/shared/store/selectors/router.selectors'

import { GroupUsersGridSearch, GroupsGridSearch } from './groups-grid-search'

import { AccessManagementStoreDeps } from '../access-management-store.types'

type UsersFeatureInput = {
  userGroupsResource: ResourceRef<AccessManagementGroup[] | undefined>
}

// The return type of signalStoreFeature is an inferred generic that must not be widened.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function withGroupsFeature() {
  return signalStoreFeature(
    { props: type<AccessManagementStoreDeps & UsersFeatureInput>() },
    withState({ groupsLoading: false }),
    withProps((store) => ({
      groupsGridSearch: new GroupsGridSearch(),
      selectedGroupId: toSignal(
        store._ngrxStore.select(selectAllRouteParams).pipe(
          map((params) => params.selectedGroupId ?? null),
          distinctUntilChanged()
        )
      ),
      getGroupAssetSummaries(groupId: string): Observable<AccessManagementAssetSummary[]> {
        patchState(store, { groupsLoading: true })

        return store._groupsApi.getGroupAssetSummaries(groupId).pipe(
          switchMap((groupAssets) => {
            const appIds = groupAssets.map((asset) => asset.id)

            return store._applicationsService
              .getApplicationSummaryByIds(appIds)
              .pipe(map((appSummaries) => groupMapToAssetSummary(groupAssets, appSummaries)))
          }),
          finalize(() => patchState(store, { groupsLoading: false })),
          catchError((error) => {
            store._toastMessageService.showErrorMessage(
              `Failed to get group assets: ${error.error?.title ? error.error.title : 'Error message'}`
            )
            return EMPTY
          })
        )
      },
      getGroupMemberCandidates(
        groupId: string,
        filterKey: string
      ): Observable<AccessManagementUserSummary[]> {
        return store._groupsApi.getGroupMemberCandidates(groupId, filterKey).pipe(
          catchError((error) => {
            store._toastMessageService.showErrorMessage(
              `Failed to get group member candidates: ${error.error?.title ? error.error.title : 'Error message'}`
            )

            return EMPTY
          })
        )
      },
      getGroupSolutionAccessAssets(
        groupId: string,
        filterKey: string
      ): Observable<GroupSolutionAccessAsset[]> {
        return store._groupsApi.getGroupSolutionAccessAssets(groupId, filterKey).pipe(
          catchError((error) => {
            store._toastMessageService.showErrorMessage(
              `Failed to get group solution access assets: ${error.error?.title ? error.error.title : 'Error message'}`
            )

            return EMPTY
          })
        )
      },
      getGroupAssetCandidates(
        groupId: string,
        filterKey: string
      ): Observable<GroupSolutionAccessAssetCandidate[]> {
        return store._groupsApi.getGroupAssetCandidates(groupId, filterKey).pipe(
          map((candidates) =>
            candidates.map((candidate) => ({
              ...candidate,
              id: `${candidate.workspaceId}-${candidate.assetId}`,
            }))
          ),
          catchError((error) => {
            store._toastMessageService.showErrorMessage(
              `Failed to get group asset candidates: ${error.error?.title ? error.error.title : 'Error message'}`
            )

            return EMPTY
          })
        )
      },
    })),
    withProps((store) => ({
      selectedGroupResource: rxResource({
        params: () => {
          const selectedGroupId = store.selectedGroupId()
          if (!selectedGroupId) {
            return undefined
          }
          return { selectedGroupId }
        },
        stream: ({ params }) => {
          const { selectedGroupId } = params

          if (!selectedGroupId) {
            return of(null)
          }

          patchState(store, { groupsLoading: true })

          return store._groupsApi.getGroup(selectedGroupId).pipe(
            finalize(() => patchState(store, { groupsLoading: false })),
            catchError((error) => {
              store._toastMessageService.showErrorMessage(
                `Failed to get group: ${error.error?.title ? error.error.title : 'Error message'}`
              )

              return EMPTY
            })
          )
        },
      }),
      groupSolutionAccessAssets: rxResource({
        params: () => {
          const selectedGroupId = store.selectedGroupId()
          if (!selectedGroupId) {
            return undefined
          }
          return {
            selectedGroupId,
          }
        },
        stream: ({ params: { selectedGroupId } }) =>
          store._groupsApi
            .getGroupSolutionAccessAssets(selectedGroupId, '')
            .pipe(
              map((assets) =>
                assets.map((asset) => ({ ...asset, id: `${asset.workspaceId}-${asset.assetId}` }))
              )
            ),
      }),
      groupUsersGridSearch: linkedSignal<string | null, GroupUsersGridSearch | undefined>({
        source: () => store.selectedGroupId() ?? undefined,
        computation: (selectedGroupId, previous) => {
          previous?.value?.manualDestroy()
          return selectedGroupId
            ? untracked(() => new GroupUsersGridSearch(selectedGroupId))
            : undefined
        },
      }),
    })),
    withComputed((store) => ({
      selectedGroup: computed(() => store.selectedGroupResource.value()),
      groupSolutionAccessAssetsTotalElements: computed(
        () => store.groupSolutionAccessAssets.value()?.length || 0
      ),
    })),
    withMethods((store) => ({
      createGroup: async (
        groupPayload: AccessManagementGroupPayload
      ): Promise<AccessManagementGroup> => {
        patchState(store, { groupsLoading: true })

        try {
          const newGroup = await lastValueFrom(store._groupsApi.createGroup(groupPayload))

          store._toastMessageService.showSuccessMessage('Group created successfully')
          patchState(store, { groupsLoading: false })

          return newGroup
        } catch (error) {
          store._toastMessageService.showErrorMessage(
            `Failed to create group: ${error.error?.title ? error.error.title : 'Error message'}`
          )

          patchState(store, { groupsLoading: false })
          return null
        }
      },
      updateGroup: async (
        groupId: string,
        groupPayload: AccessManagementGroupPayload
      ): Promise<AccessManagementGroup> => {
        patchState(store, { groupsLoading: true })

        try {
          const updatedGroup = await lastValueFrom(
            store._groupsApi.updateGroup(groupId, groupPayload)
          )
          store.groupsGridSearch.refresh()

          store._toastMessageService.showSuccessMessage('Group updated successfully')
          patchState(store, { groupsLoading: false })
          return updatedGroup
        } catch (error) {
          store._toastMessageService.showErrorMessage(
            `Failed to update group: ${error.error?.title ? error.error.title : 'Error message'}`
          )

          patchState(store, { groupsLoading: false })
          return null
        }
      },
      deleteGroup: async (groupId: string, groupName: string) => {
        try {
          await lastValueFrom(store._groupsApi.deleteGroup(groupId))
          store.groupsGridSearch.refresh()

          store._toastMessageService.showSuccessMessage(
            `Group ${groupName} was deleted. Access to associated resources was revoked.`
          )

          return true
        } catch (error) {
          store._toastMessageService.showErrorMessage(
            `Failed to delete group ${groupName}: ${error.error?.title ? error.error.title : 'Error message'}`
          )

          return false
        }
      },
      addUsersToGroup: async (users: AccessManagementUserSummary[]) => {
        patchState(store, { groupsLoading: true })
        const selectedGroupId = store.selectedGroupId()

        try {
          await lastValueFrom(store._groupsApi.addUsersToGroup(selectedGroupId, users))

          store.groupUsersGridSearch().refresh()
          patchState(store, { groupsLoading: false })
          store._toastMessageService.showSuccessMessage('Users added successfully to group')
          return true
        } catch (error) {
          patchState(store, { groupsLoading: false })

          store._toastMessageService.showErrorMessage(
            `Failed to add members to group: ${error.error?.title ? error.error.title : 'Error message'}`
          )

          return false
        }
      },
      removeUserFromGroup: async (groupId: string, user: AccessManagementUser) => {
        const selectedGroupId = groupId || store.selectedGroupId()

        try {
          await lastValueFrom(store._groupsApi.removeUserFromGroup(selectedGroupId, user))

          store.groupUsersGridSearch()?.refresh()

          store.userGroupsResource.update((userGroups) =>
            groupId ? userGroups.filter((userGroup) => userGroup.id !== groupId) : userGroups
          )

          store._toastMessageService.showSuccessMessage(`Member removed from group`)
        } catch (error) {
          store._toastMessageService.showErrorMessage(
            `Failed to remove member from group: ${error.error?.title ? error.error.title : 'Error message'}`
          )
        }
      },
    })),
    withHooks({
      onDestroy(store) {
        store.groupsGridSearch.manualDestroy()
        store.groupUsersGridSearch()?.manualDestroy()
      },
    })
  )
}
